import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Table, Button } from "react-bootstrap";
import "./StudentPerformance.css";
import TableSkeleton from "../../common/TableSkeleton";
import TableError from "../../common/TableError";
import DashboardStats from "../../../common/Dashboard/DashboardStats";
import DashboardStatsSkeleton from "../../../common/Dashboard/DashboardStatsSkeleton";
import TablePagination from "../../TeacherAdmin/pagination/TablePagination";
import { useGetTeacherReportById } from "../../../../hooks/school/teacher/useGetTeacherReportById";
import { ITEMS_PER_PAGE } from "../../../../constant/constant";
import "../../../../common/styles/table.css";
import { handleSmartBack } from "../../../../utils/navigationHelper";

/**
 * TeacherPerformanceSchool - Teacher performance component for School Admin routes.
 * Uses the school-specific useGetTeacherReportById hook.
 * teacherId comes from URL params.
 */
const TeacherPerformanceSchool = () => {
    const navigate = useNavigate();
    const { teacherId } = useParams();
    const [currentPage, setCurrentPage] = useState(1);

    const { loading, error, reportData, getTeacherReportById, metaData } =
        useGetTeacherReportById();

    // Fetch teacher data on mount and page change
    useEffect(() => {
        if (teacherId) {
            getTeacherReportById(teacherId, currentPage, ITEMS_PER_PAGE);
        }
    }, [teacherId, currentPage]);

    const handleRefresh = () => {
        if (teacherId) {
            getTeacherReportById(teacherId, currentPage, ITEMS_PER_PAGE);
        }
    };

    // Generate initials from name
    const getInitials = (name) => {
        if (!name) return "?";
        return name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase();
    };

    const teacherDetail = reportData?.teacher;
    const classes = reportData?.classes?.data || [];
    const totalPages = metaData?.last_page || 1;

    // Check if initial load (no data yet)
    const isInitialLoad = reportData === undefined;

    // Stats data for DashboardStats component
    const stats = [
        {
            label: "Total Classes",
            value:
                loading && isInitialLoad ? "..." : teacherDetail?.total_classes || 0,
            icon: "fa-chalkboard-user",
            colorClass: "dashboard-stat-card-blue",
        },
        {
            label: "Overall Progress",
            value:
                loading && isInitialLoad
                    ? "..."
                    : `${teacherDetail?.overall_progress || 0}%`,
            icon: "fa-chart-line",
            colorClass: "dashboard-stat-card-peach",
        },
        {
            label: "Quiz Pass Rate",
            value:
                loading && isInitialLoad
                    ? "..."
                    : `${teacherDetail?.overall_quiz_pass_rate || 0}%`,
            icon: "fa-check-circle",
            colorClass: "dashboard-stat-card-purple",
        },
        {
            label: "Completion Rate",
            value:
                loading && isInitialLoad
                    ? "..."
                    : `${teacherDetail?.completion_rate || 0}%`,
            icon: "fa-flag-checkered",
            colorClass: "dashboard-stat-card-green",
        },
    ];

    // Profile Skeleton Component
    const ProfileSkeleton = () => (
        <div className="student-profile-card">
            <div className="student-profile-left">
                <div className="skeleton-box skeleton-avatar"></div>
                <div className="student-info">
                    <div
                        className="skeleton-box"
                        style={{ width: 150, height: 24, marginBottom: 8 }}
                    ></div>
                    <div
                        className="skeleton-box"
                        style={{ width: 200, height: 16 }}
                    ></div>
                </div>
            </div>
            <div className="student-profile-right">
                <div
                    className="skeleton-box"
                    style={{ width: 120, height: 40, borderRadius: 5 }}
                ></div>
            </div>
        </div>
    );

    const handleBack = () => {
        handleSmartBack(navigate, "/school/report/teachers");
    };

    // Show skeleton on initial load
    if (loading && isInitialLoad) {
        return (
            <div className="student-performance-container">
                <div className="pt-2">
                    <button className="back-btn" onClick={handleBack}>
                        <i className="bi bi-arrow-left"></i> Back to Teachers
                    </button>
                </div>
                <ProfileSkeleton />
                <DashboardStatsSkeleton count={4} />
                <TableSkeleton rows={5} columns={5} />
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="student-performance-container">
                <div className="pt-2">
                    <button className="back-btn" onClick={handleBack}>
                        <i className="bi bi-arrow-left"></i> Back to Teachers
                    </button>
                </div>
                <TableError
                    error={error}
                    handleRefresh={handleRefresh}
                    loading={loading}
                />
            </div>
        );
    }

    // Show 404 if no data found
    if (!loading && reportData === null) {
        return (
            <div className="student-performance-container">
                <div className="pt-2">
                    <button className="back-btn" onClick={handleBack}>
                        <i className="bi bi-arrow-left"></i> Back to Teachers
                    </button>
                </div>
                <div className="text-center py-5">
                    <h1 className="text-muted" style={{ fontSize: "72px" }}>
                        404
                    </h1>
                    <h4 className="mb-3">Teacher Not Found</h4>
                    <p className="text-muted mb-4">
                        The teacher you're looking for doesn't exist or may have been
                        removed.
                    </p>
                    <Button variant="primary" onClick={handleBack}>
                        Go Back to Teachers
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <div className="student-performance-container">
            {/* Back Button */}
            <div className="pt-2">
                <button className="back-btn" onClick={handleBack}>
                    <i className="bi bi-arrow-left"></i> Back to Teachers
                </button>
            </div>

            {/* Teacher Profile Header */}
            <div className="student-profile-card">
                <div className="student-profile-left">
                    <div
                        className="student-avatar-circle"
                        style={{ backgroundColor: "#ff7f0e" }}
                    >
                        {getInitials(teacherDetail?.name)}
                    </div>
                    <div className="student-info">
                        <h3>{teacherDetail?.name || "-"}</h3>
                        <div className="student-details-row">
                            <span>
                                Email: <strong>{teacherDetail?.email || "-"}</strong>
                            </span>
                        </div>
                    </div>
                </div>
                {/* <div className="student-profile-right">
                    <Button className="download-report-btn">View Profile</Button>
                </div> */}
            </div>

            {/* Stats Cards using DashboardStats */}
            <DashboardStats stats={stats} />

            {/* Classes Table */}
            {classes.length === 0 ? (
                <div className="alert alert-info mt-0" role="alert">
                    No classes assigned to this teacher.
                </div>
            ) : (
                <div className="custom-table-container mt-0">
                    <div className="custom-table-wrapper">
                        <Table hover className="custom-table">
                            <thead className="custom-table-head">
                                <tr>
                                    <th>Class Name</th>
                                    <th>Avg Progress</th>
                                    <th>80% Progress</th>
                                    <th>100% Progress</th>
                                    <th>Avg Quiz Score</th>
                                    <th>Avg Attempts</th>
                                </tr>
                            </thead>
                            <tbody>
                                {classes.map((classItem, index) => (
                                    <tr key={classItem.id || index}>
                                        <td>{classItem.name || "-"}</td>
                                        <td className="text-center">
                                            {classItem.avg_progress ?? 0}%
                                        </td>
                                        <td className="text-center">
                                            {classItem.progress_80_percent ?? 0}
                                        </td>
                                        <td className="text-center">
                                            {classItem.progress_100_percent ?? 0}
                                        </td>
                                        <td className="text-center">
                                            {classItem.avg_quiz_score ?? 0}
                                        </td>
                                        <td className="text-center">
                                            {classItem.avg_attempts ?? 0}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>

                    <TablePagination
                        totalPage={totalPages}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        disabled={loading}
                    />
                </div>
            )}
        </div>
    );
};

export default TeacherPerformanceSchool;
